import React, { useCallback, useEffect, useMemo, useState } from 'react';
import noop from 'lodash/noop';
import { graphql, useFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { UNSAFE_useMediaQuery as useMediaQuery } from '@atlaskit/primitives/responsive';
import AssigneeFilter, {
	type AssigneeFilterProps,
} from '@atlassian/jira-filters/src/ui/filters/assignee/main.tsx';
import {
	fireOperationalAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { ASSIGNEE, SOURCE_NAME } from '../../common/constants.tsx';
import { useClauseUpdater } from '../../controllers/ast/use-clause-updater/index.tsx';
import { useSelectedValues } from '../../controllers/ast/use-selected-values/index.tsx';
import { useASTActions } from '../../controllers/ast/index.tsx';
import { useIsAssigneePickerEnabled } from '../../controllers/ast/use-is-assignee-picker-enabled/index.tsx';
import type {
	AssigneePicker$data,
	AssigneePicker$key,
} from './__generated__/AssigneePicker.graphql';
import messages from './messages.tsx';

type AccountId = string;

export type Props = {
	fragmentRef: AssigneePicker$key | null | undefined;
	isAddPeopleButtonEnabled?: boolean;
	projectId?: number;
	projectKey?: string;
	projectName?: string;
	isResponsive: boolean;
	isModeSwitcherEnabled?: boolean;
};

const useAssignees = (data: AssigneePicker$data | null | undefined) => {
	return useMemo(() => {
		const result: AssigneeFilterProps['users'] = [];

		data?.jqlBuilder?.fieldValues.edges?.forEach((edge) => {
			if (edge?.node != null) {
				// empty is normalized to lowercase for easier AST and analytics matching
				const id = edge.node.__typename === 'JiraJqlEmptyFieldValue' ? 'empty' : edge.node.jqlTerm;

				result.push({
					id,
					displayName: edge.node.displayName,
					avatarUrl: edge.node.user?.picture,
				});
			}
		});

		return result;
	}, [data?.jqlBuilder?.fieldValues?.edges]);
};

const MAX_USERS = 7;

const useMaxUsers = (isResponsive?: boolean) => {
	const [maxUsers, setMaxUsers] = useState(MAX_USERS);

	const largeScreenMQ = useMediaQuery('above.lg', (_event) => {
		if (fg('jira_jif_jam_filter_refinement_responsive')) {
			updateMaxUsers();
		}
	});
	const mediumScreenMQ = useMediaQuery('above.md', () => {
		if (fg('jira_jif_jam_filter_refinement_responsive')) {
			updateMaxUsers();
		}
	});

	const updateMaxUsers = useCallback(() => {
		if (!fg('jira_jif_jam_filter_refinement_responsive')) {
			return;
		}

		if (!isResponsive && fg('list_lovability_improving_filters')) {
			return;
		}

		if (largeScreenMQ?.matches) {
			setMaxUsers(7); // Large screens (> 1440px)
		} else if (mediumScreenMQ?.matches) {
			setMaxUsers(5); // Medium screens (1024px - 1440px)
		} else {
			setMaxUsers(3); // Small screens (< 1024px)
		}
	}, [largeScreenMQ?.matches, mediumScreenMQ?.matches, isResponsive]);

	// make sure correct number of users is displayed on mount
	useEffect(() => {
		updateMaxUsers();
	}, [updateMaxUsers]);

	return maxUsers;
};

const AddPeopleModal = () => <></>;

function getAnalyticsData(
	currentUserAccountId: string | null,
	avatarAccountId: string,
	amountOfSelectedOptionsOfFieldType: number,
) {
	return {
		action: 'clicked',
		actionSubject: 'avatar',
		actionSubjectId: 'chooseAssignee',
		attributes: {
			isMe: avatarAccountId === currentUserAccountId,
			isUnassigned: avatarAccountId === 'empty',
			amountOfSelectedOptionsOfFieldType,
		},
	};
}

export const AssigneePicker = ({
	fragmentRef,
	isAddPeopleButtonEnabled = false,
	projectId,
	projectKey,
	projectName,
	isResponsive,
	isModeSwitcherEnabled = false,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const currentUserAccountId = useAccountId();

	// TODO: shared assignee picker doesn't have pagination, re-evaluate number of field values we fetch based on data
	const data = useFragment<AssigneePicker$key>(
		graphql`
			fragment AssigneePicker on JiraQuery
			@argumentDefinitions(cloudId: { type: "ID!" }, scope: { type: "JiraJqlScopeInput" }) {
				jqlBuilder(cloudId: $cloudId) {
					fieldValues(jqlTerm: "assignee", first: 100, scope: $scope) @required(action: THROW) {
						edges {
							node {
								__typename
								jqlTerm
								displayName
								... on JiraJqlUserFieldValue {
									user {
										picture
									}
								}
							}
						}
					}
				}
			}
		`,
		fragmentRef,
	);

	const assignees = useAssignees(data);

	const maxUsers = useMaxUsers(isResponsive);

	const selectedAssignees = useSelectedValues(ASSIGNEE);

	const { addValueToClause, removeValueFromClause, getTotalCount } = useClauseUpdater(ASSIGNEE);

	const { updateJql } = useASTActions();

	const isAssigneePickerEnabled = useIsAssigneePickerEnabled();

	const { formatMessage } = useIntl();

	useEffect(() => {
		if (assignees.length === 0) {
			fireOperationalAnalytics(
				createAnalyticsEvent({}),
				'filterRefinementAssigneePicker emptyAssigneeList',
			);
		}
	}, [assignees.length, createAnalyticsEvent]);

	const onToggle = useCallback(
		(accountId: AccountId) => {
			updateJql({
				jql: selectedAssignees.includes(accountId)
					? removeValueFromClause(accountId)
					: addValueToClause(accountId),
				fieldType: ASSIGNEE,
				amountOfSelectedOptionsOfFieldType: getTotalCount(),
			});

			fireUIAnalytics(
				createAnalyticsEvent({}),
				getAnalyticsData(currentUserAccountId, accountId, getTotalCount()),
			);
		},
		[
			addValueToClause,
			createAnalyticsEvent,
			currentUserAccountId,
			getTotalCount,
			removeValueFromClause,
			selectedAssignees,
			updateJql,
		],
	);

	return (
		<Box xcss={[isAddPeopleButtonEnabled && ContainerStyleWithAddPeopleButton]}>
			<AssigneeFilter
				screenName={SOURCE_NAME}
				isAddPeopleButtonEnabled={isAddPeopleButtonEnabled}
				maxUsers={fg('jira_jif_jam_filter_refinement_responsive') ? maxUsers : 7}
				onAssigneeMenuOpen={noop}
				onToggle={onToggle}
				// only used in AddPeopleModal, setting to -1 to avoid typescript problem
				projectId={projectId ?? -1}
				projectKey={projectKey}
				selectedUsers={selectedAssignees}
				projectName={projectName}
				users={assignees}
				// no need to add people modal, since it is already within AssigneeFilter
				AddPeopleModal={AddPeopleModal}
				{...(isModeSwitcherEnabled && fg('list_lovability_improving_filters')
					? {
							isDisabled: !isAssigneePickerEnabled,
							disabledPopperMessage: formatMessage(messages.disabledPopperText),
						}
					: {})}
			/>
		</Box>
	);
};

const ContainerStyleWithAddPeopleButton = xcss({
	// to align with 10px spacing between people avatars and addPeople button
	paddingInlineEnd: 'space.025',
	display: 'flex',
	alignItems: 'center',
});
